import type { MouseEvent } from 'react';
import { atMinutes, firstFreeOfDay, formatMinutes, isFreeAt, type Interval } from '../../../lib/calendar/team/availability';
import { layoutLanes } from '../../../lib/calendar/team/layout';
import type { TeamColumnData } from '../../../lib/calendar/team/useTeamColumns';
import { PersonName } from '../../shared/PersonName';

/** Ore mostrate se nessuno lavora prima delle 8 o dopo le 19 (vedi gridRange) */
export const GRID_FROM = 8 * 60;
export const GRID_TO = 19 * 60;

interface Props {
  col: TeamColumnData;
  day: Date;
  hourHeight: number;
  /** orario cercato (minuti) e durata: le colonne occupate si attenuano */
  freeAt: number | null;
  duration: number;
  compact?: boolean;
  /** ore della griglia (minuti), uguali per tutte le colonne */
  range?: Interval;
  onPick?: (slot: { userId: string; start: Date; end: Date }) => void;
  /** clic su un'indisponibilità (si apre il drawer) */
  onOpenUnavailability?: (id: string) => void;
}


export function TeamColumnHeader({ col, freeAt, duration, compact }: Pick<Props, 'col' | 'freeAt' | 'duration' | 'compact'>) {
  const first = firstFreeOfDay(col.busy, col.work);
  const n = col.appointments.filter(a => !a.block).length;
  const dim = freeAt !== null && !isFreeAt(col.busy, col.work, freeAt, duration);
  return (
    <div className={`px-2 py-1.5 text-center ${dim ? 'opacity-40' : ''}`}>
      <div className="flex items-center justify-center gap-1.5 text-[13px] font-semibold text-[#37352F] dark:text-white">
        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: col.color }} />
        <PersonName name={col.name} />
      </div>
      <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-[#787774]`}>
        {n} {n === 1 ? 'appuntamento' : 'appuntamenti'} · {!col.work ? 'non lavora' : first !== null ? `disponibile alle ${formatMinutes(first)}` : 'nessun orario libero'}
      </div>
    </div>
  );
}

export function TeamColumn({ col, day, hourHeight: h, freeAt, duration, onPick, onOpenUnavailability, range = { start: GRID_FROM, end: GRID_TO } }: Props) {
  const { start: from, end: to } = range;
  const y = (m: number, hh: number) => ((Math.max(from, Math.min(to, m)) - from) / 60) * hh;
  const total = ((to - from) / 60) * h;
  const dim = freeAt !== null && !isFreeAt(col.busy, col.work, freeAt, duration);
  const off: Interval[] = col.work
    ? [{ start: from, end: col.work.start }, { start: col.work.end, end: to }]
    : [{ start: from, end: to }];

  return (
    <div className={`relative border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 ${dim ? 'opacity-40' : ''}`} style={{ height: total, backgroundImage: 'linear-gradient(to bottom, rgba(55,53,47,0.07) 1px, transparent 1px)', backgroundSize: `100% ${h}px` }}>
      {off.filter(o => o.end > o.start).map((o, i) => (
        <div key={i} className="absolute inset-x-0 bg-[#F1F0EE] dark:bg-white/[0.04]" style={{ top: y(o.start, h), height: y(o.end, h) - y(o.start, h) }} />
      ))}
      {col.free.map((f, i) => {
        const top = y(f.start, h);
        const height = y(f.end, h) - top;
        if (height <= 0) return null;
        const pick = (ev: MouseEvent<HTMLButtonElement>) => {
          if (!onPick) return;
          const r = ev.currentTarget.getBoundingClientRect();
          const raw = f.start + ((ev.clientY - r.top) / h) * 60;
          const start = Math.max(f.start, Math.min(Math.floor(raw / 15) * 15, f.end - 15));
          const end = Math.min(f.end, start + duration);
          onPick({ userId: col.id, start: atMinutes(day, start), end: atMinutes(day, end) });
        };
        return (
          <button
            key={i}
            type="button"
            disabled={!onPick}
            onClick={pick}
            title={`Libero dalle ${formatMinutes(f.start)} alle ${formatMinutes(f.end)}`}
            className={`absolute inset-x-0.5 rounded-md border border-dashed border-[#0F7B6C]/60 bg-[#0F7B6C]/[0.06] text-left text-[10px] text-[#0F7B6C] ${onPick ? 'cursor-pointer hover:bg-[#0F7B6C]/[0.14]' : 'cursor-default'}`}
            style={{ top: top + 1, height: Math.max(0, height - 2) }}
          >
            {height >= 22 && <span className="px-1">Libero</span>}
          </button>
        );
      })}
      {layoutLanes(col.appointments).map(({ item: a, lane, lanes }) => {
        const top = y(a.start, h);
        const height = y(a.end, h) - top;
        if (height <= 0) return null;
        const open = a.unavailabilityId && onOpenUnavailability ? () => onOpenUnavailability(a.unavailabilityId!) : undefined;
        return (
          <div
            key={a.id}
            title={`${a.title} (${formatMinutes(a.start)}-${formatMinutes(a.end)})`}
            onClick={open}
            role={open ? 'button' : undefined}
            className={`absolute overflow-hidden rounded-[7px] px-1.5 py-0.5 text-[11px] leading-tight ${open ? 'cursor-pointer hover:brightness-95' : ''} ${a.busyOnly ? 'border border-dashed border-[#9B9A97] bg-[#E9E8E5] text-[#787774] dark:bg-white/10' : 'text-[#1a1a1a] dark:text-white'} ${a.done ? 'opacity-60' : ''}`}
            style={{
              top: top + 1, height: Math.max(16, height - 2),
              left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`,
              // Stesso stile del calendario: fondo tenue del colore della persona, angoli arrotondati
              ...(a.busyOnly ? {} : { background: `${a.color}2E` }),
            }}
          >
            <div className="truncate font-medium">
              {a.outcome && (
                <span data-event-outcome className="mr-1 inline-block max-w-full truncate rounded-[3px] bg-[rgba(15,123,108,0.16)] px-1 align-middle text-[9.5px] font-semibold leading-[1.35] text-[#0B5E52] no-underline">{a.outcome}</span>
              )}
              <span className={a.done ? 'line-through' : ''}>{a.title}</span>
            </div>
            {height >= 34 && <div className="truncate opacity-80">{formatMinutes(a.start)}-{formatMinutes(a.end)}</div>}
          </div>
        );
      })}
      {freeAt !== null && freeAt >= from && freeAt <= to && (
        <div className="pointer-events-none absolute inset-x-0 border-t-2 border-[#2383E2]" style={{ top: y(freeAt, h) }} />
      )}
    </div>
  );
}
